import RrpPricePointsDropdown from '@/pages/price-planning/create-price-page/components/rrp-price-points-dropdown';
import { filterNumeric } from '@/utils/number';
import { TextField } from '@mui/material';
import type {
  MRT_Cell,
  MRT_Row,
  MRT_TableInstance,
} from 'material-react-table';
import type { Dispatch, SetStateAction } from 'react';
import {
  formatDecimal,
  priceDisplayDecimals,
  rmDisplayDecimals,
} from './table-format-helpers';
import type { BaseRowData, BaseRowDataForDisplay } from './table-types';

type EditCellInputProps<
  TRow extends BaseRowData,
  TRowForDisplay extends BaseRowDataForDisplay,
  TColumn extends string & keyof TRow,
> = {
  cell: MRT_Cell<TRowForDisplay>;
  column: { id: string };
  row: MRT_Row<TRowForDisplay>;
  table: MRT_TableInstance<TRowForDisplay>;
  columnKey: TColumn;
  data: Map<string, TRow>;
  pendingEditKey: string | null;
  planningYear: number;
  setPendingEditKey: Dispatch<SetStateAction<string | null>>;
  setCellToFocus: Dispatch<SetStateAction<string | null>>;
  handleCellEdit: (
    cellValue: string,
    columnKey: string,
    rowIndex: MRT_Row<TRowForDisplay>,
  ) => void;
};

export const EditCellInput = <
  TRow extends BaseRowData,
  TRowForDisplay extends BaseRowDataForDisplay,
  TColumn extends string & keyof TRow,
>({
  cell,
  column,
  row,
  table,
  columnKey,
  data,
  pendingEditKey,
  planningYear,
  setPendingEditKey,
  setCellToFocus,
  handleCellEdit,
}: EditCellInputProps<TRow, TRowForDisplay, TColumn>) => {
  const rawRow = data.get(row.original.primaryKey);
  const rawValue = rawRow?.[columnKey as keyof TRow];
  const isNumericColumn = ['rrp', 'rm', 'lp'].includes(column.id);
  // LP/RRP are shown with the row's own currency decimals; extra typed decimals are rounded on commit
  const displayDecimals = rawRow?.currencyDecimals ?? priceDisplayDecimals;

  const getFieldValue = () => {
    if (pendingEditKey !== null) {
      return isNumericColumn ? filterNumeric(pendingEditKey) : pendingEditKey;
    }
    switch (column.id) {
      case 'rm':
        return formatDecimal(Number(rawValue) * 100, rmDisplayDecimals, true);
      case 'lp':
        return formatDecimal(Number(rawValue), displayDecimals, true);
      case 'rrp':
        return formatDecimal(Number(rawValue), displayDecimals, true);
      default:
        return rawValue;
    }
  };

  const initialValue = getFieldValue();

  if (column.id === 'rrp') {
    return (
      <RrpPricePointsDropdown
        priceUnitId={row.original.priceUnitId}
        planningYear={planningYear}
        rrp={rawValue as number}
        displayValue={String(initialValue)}
        onSelect={(val) => handleCellEdit(val, columnKey as string, cell.row)}
        onClose={() => {
          table.setEditingCell(null);
          setPendingEditKey(null);
          setCellToFocus(cell.id);
        }}
        pendingEditKey={pendingEditKey}
      />
    );
  }

  return (
    <TextField
      key={cell.id}
      defaultValue={initialValue}
      onChange={(e) => {
        if (isNumericColumn) {
          e.target.value = filterNumeric(e.target.value);
        }
      }}
      onBlur={(e) => {
        handleCellEdit(e.target.value, columnKey as string, cell.row);
        table.setEditingCell(null);
      }}
      variant="standard"
      autoFocus
      onFocus={(e) => {
        if (pendingEditKey === null) {
          e.target.select();
        } else {
          setPendingEditKey(null);
        }
      }}
      size="small"
      sx={{ fontSize: '11px' }}
      slotProps={{
        input: {
          sx: { fontSize: '11px' },
        },
      }}
    />
  );
};
